import { useEffect, useRef, type RefObject } from 'react';

/** How long the open panel may sit without focus before it recoils. */
export const AUTO_MINIMIZE_MS = 60000;

/**
 * A control that becomes `disabled` (the send button while an answer streams) or
 * is unmounted throws focus at the body without anyone leaving the widget. That
 * "ghost blur" must not start the countdown.
 */
function isGhostBlur(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (!target.isConnected) return true;
  return (target as { disabled?: boolean }).disabled === true;
}

/**
 * Collapse the panel after `delayMs` with the focus outside `containerRef`.
 * The clock starts when focus actually leaves and is cancelled the moment it
 * comes back — it never restarts while the user works elsewhere, so "outside for
 * a minute" means sixty continuous seconds. Focus resting INSIDE the panel
 * (the composer, a message, a control) never starts the countdown at all.
 */
export function useAutoMinimize(
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  onMinimize: () => void,
  delayMs: number = AUTO_MINIMIZE_MS,
): void {
  const minimizeRef = useRef(onMinimize);
  minimizeRef.current = onMinimize;

  useEffect(() => {
    if (!active) return;
    let timer = 0;

    const cancel = (): void => {
      window.clearTimeout(timer);
      timer = 0;
    };
    const start = (): void => {
      if (timer !== 0) return;
      timer = window.setTimeout(() => minimizeRef.current(), delayMs);
    };
    const inside = (node: EventTarget | null): boolean =>
      node instanceof Node && (containerRef.current?.contains(node) ?? false);

    const onFocusOut = (event: FocusEvent): void => {
      if (inside(event.relatedTarget)) return;
      if (event.relatedTarget === null && isGhostBlur(event.target)) return;
      start();
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (inside(event.target)) cancel();
      else start();
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      cancel();
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [active, containerRef, delayMs]);
}
